<template>
  <div :class="checkboxGroupClasses">
    <!-- Select All option -->
    <div v-if="showSelectAll" class="themed-checkbox-group__select-all">
      <themed-checkbox
        :checked="allSelected"
        @change="toggleAll"
        :label="selectAllLabel"
      />
    </div>
    
    <!-- Individual checkboxes -->
    <div
      v-for="option in normalizedOptions"
      :key="option.value"
      class="themed-checkbox-group__option"
    >
      <themed-checkbox
        :checked="isSelected(option.value)"
        @change="(checked) => toggleOption(option.value, checked)"
        :disabled="disabled || option.disabled"
      />
      <div v-if="$scopedSlots.option" class="themed-checkbox-group__option-content">
        <slot name="option" :option="option" />
      </div>
      <span v-else class="themed-checkbox-group__option-label">
        {{ option.label }}
      </span>
    </div>
  </div>
</template>

<script>
import ThemedCheckbox from './ThemedCheckbox.vue';

export default {
  name: 'ThemedCheckboxGroup',
  
  components: {
    ThemedCheckbox
  },
  
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      required: true,
      validator: (options) => {
        return options.every(opt => {
          if (typeof opt === 'object') {
            return Object.prototype.hasOwnProperty.call(opt, 'value') && Object.prototype.hasOwnProperty.call(opt, 'label');
          }
          return typeof opt === 'string' || typeof opt === 'number';
        });
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    layout: {
      type: String,
      default: 'vertical',
      validator: (v) => ['vertical', 'horizontal', 'grid'].includes(v)
    },
    showSelectAll: {
      type: Boolean,
      default: false
    },
    selectAllLabel: {
      type: String,
      default: 'Select All'
    },
    trackBy: {
      type: String,
      default: 'value'
    }
  },
  
  computed: {
    checkboxGroupClasses() {
      return [
        'themed-checkbox-group',
        `themed-checkbox-group--${this.layout}`
      ];
    },
    
    normalizedOptions() {
      return this.options.map(option => {
        if (typeof option === 'object') {
          return {
            value: option[this.trackBy] || option.value,
            label: option.label,
            disabled: option.disabled || false,
            ...option // Keep original data for slot
          };
        }
        // If option is a string or number, use it as both value and label
        return {
          value: option,
          label: option,
          disabled: false
        };
      });
    },
    
    allSelected() {
      if (this.normalizedOptions.length === 0) return false;
      return this.normalizedOptions.every(opt => 
        !opt.disabled && this.isSelected(opt.value)
      );
    }
  },
  
  methods: {
    isSelected(value) {
      return this.value.includes(value);
    },
    
    toggleOption(value, checked) {
      let newValue;
      if (checked) {
        newValue = [...this.value, value];
      } else {
        newValue = this.value.filter(v => v !== value);
      }
      this.$emit('input', newValue);
      this.$emit('change', newValue);
    },
    
    toggleAll(checked) {
      if (checked) {
        // Select all non-disabled options
        const allValues = this.normalizedOptions
          .filter(opt => !opt.disabled)
          .map(opt => opt.value);
        this.$emit('input', allValues);
        this.$emit('change', allValues);
      } else {
        // Deselect all
        this.$emit('input', []);
        this.$emit('change', []);
      }
    }
  }
};
</script>
